import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Truck, Wheat } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/sections/Footer';
import Seo from '../components/Seo';
import { businessInfo, locations, productionCenter } from '../mock';
import { SITE_URL, getBreadcrumbJsonLd, getOrganizationJsonLd } from '../lib/structuredData';

// ─────────────────────────────────────────────────────────────────────────────
// HISTORIA REAL DE BRAZOLOVERS
//
// Contada por Carolina Parra, fundadora. Todo lo de aquí es real: si algún dato
// cambia (una sede nueva, un reconocimiento), se edita AQUÍ y la línea de
// tiempo se actualiza sola. Si `HISTORIA` vuelve a `null`, la sección
// simplemente no se renderiza.
// ─────────────────────────────────────────────────────────────────────────────
const HISTORIA = {
  parrafos: [
    'Brazolovers nació de una convicción clara: la pastelería tradicional merecía ser rescatada del olvido y llevada a su máxima expresión.',
    'Durante años, el brazo gitano quedó relegado en muchas vitrinas a preparaciones genéricas, pesadas o descuidadas. Carolina Parra, chef pastelera formada en Venezuela y Ecuador, vio en ese clásico algo completamente distinto: un lienzo en blanco para la alta repostería. Su visión era reivindicarlo, despojarlo de excesos industriales y convertirlo en una experiencia delicada y memorable.',
    'Ese propósito tomó forma en 2020, en medio del desafío de la pandemia. Desde la cocina de casa, combinando técnica rigurosa, materia prima de primera y un equilibrio exacto de texturas y sabores, los primeros brazos gitanos empezaron a conquistar paladares que buscaban autenticidad. La respuesta fue inmediata: no era un postre más, era el renacer de un clásico artesanal.',
    'Para 2021 la pasión desbordó la cocina casera y abrimos nuestra primera sede matriz. Gracias a una comunidad que creció bocado a bocado, esa primera vitrina dio paso a dos locales en la ciudad y a un centro de producción propio, diseñado para cuidar cada lote como si fuera el primero.',
    'Hoy Brazolovers es el lugar donde la técnica pastelera y el amor por lo bien hecho se encuentran. Cada creación demuestra lo mismo: cuando el respeto por el ingrediente lidera la cocina, lo clásico se vuelve extraordinario.',
  ],
  hitos: [
    { anio: '2020', texto: 'Los primeros brazos gitanos salen de la cocina de casa, en plena pandemia.' },
    { anio: '2021', texto: 'Abre la sede matriz: el proyecto deja de ser casero y se vuelve pastelería.' },
    { anio: 'Hoy', texto: 'Dos locales en Quito, centro de producción propio y más de 40 sabores.' },
  ],
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Calificación de Google: la fuente real es /api/google-reviews, el MISMO
// endpoint que alimenta el carrusel de la home, y ya viene agregada (promedio
// ponderado y suma de reseñas de los dos locales). Los valores de mock.js solo
// sirven de respaldo mientras carga o si el API falla, para no pintar un hueco
// en el HTML prerenderizado.
const FALLBACK = {
  total: locations.reduce((acc, l) => acc + (l.googleReviews || 0), 0),
  rating: locations.reduce((acc, l) => Math.max(acc, l.googleRating || 0), 0),
};

// Semilla horneada por el prerender (script #__brazo_reviews__), mismo patrón
// que useProducts. Sin ella, el HTML prerenderizado traería el número del API y
// el cliente arrancaría con el respaldo: text mismatch (React #425).
const SEED_ID = '__brazo_reviews__';
const readSeed = () => {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById(SEED_ID);
  if (!el) return null;
  try {
    const d = JSON.parse(el.textContent);
    return d && d.rating > 0 && d.total > 0 ? d : null;
  } catch {
    return null;
  }
};

const Nosotros = () => {
  const [resenas, setResenas] = useState(() => readSeed() || FALLBACK);

  useEffect(() => {
    let vivo = true;
    fetch(`${BACKEND_URL}/api/google-reviews`)
      .then((r) => r.json())
      .then((d) => {
        const rating = Number(d?.average_rating);
        const total = Number(d?.total_reviews);
        if (!(rating > 0 && total > 0)) return;
        // Lo publicamos para que el prerender pueda sembrarlo en el HTML.
        window.__BRAZO_REVIEWS__ = { rating, total };
        if (vivo) setResenas({ rating, total });
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${SITE_URL}/nosotros/#page`,
      name: 'Nosotros — Brazolovers',
      url: `${SITE_URL}/nosotros/`,
      inLanguage: 'es-EC',
      about: { '@id': `${SITE_URL}/#organization` },
      isPartOf: { '@id': `${SITE_URL}/#website` },
    },
    // Mismo @id que la Organización global: Google fusiona los nodos, así que
    // aquí solo añadimos lo que esta página aporta (fundadora y año).
    {
      ...getOrganizationJsonLd(),
      foundingDate: '2020',
      founder: {
        '@type': 'Person',
        name: 'Carolina Parra',
        jobTitle: 'Chef pastelera y fundadora',
      },
    },
    getBreadcrumbJsonLd([
      { name: 'Inicio', path: '/' },
      { name: 'Nosotros', path: '/nosotros/' },
    ]),
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Seo
        title="Nosotros | Brazolovers — pastelería artesanal en Quito"
        description="La historia de Brazolovers: de la cocina de casa en 2020 a dos locales en Quito. Carolina Parra, chef pastelera, convirtió el brazo gitano en alta repostería artesanal con más de 40 sabores."
        path="/nosotros/"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="flex-1">
        {/* Portada */}
        <section className="container mx-auto px-4 md:px-6 pt-10 pb-6">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-chocolate mb-3">Nosotros</h1>
            <p className="text-lg md:text-xl text-chocolate-light leading-relaxed text-justify hyphens-auto">
              Brazolovers es una pastelería quiteña que hizo del <strong>brazo gitano</strong> su
              especialidad. Empezó en 2020 en la cocina de una casa y hoy son dos locales en Quito.
              Horneamos cada día, en cantidades pequeñas, para que lo que llega a tu mesa esté
              fresco de verdad.
            </p>
          </div>
        </section>

        <section className="container mx-auto px-4 md:px-6 pb-10">
          {/* Misma columna max-w-3xl que el texto: mezclar anchos hacía que la
              página se viera descuadrada de sección a sección. */}
          <div className="max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-xl aspect-[21/9] max-h-[420px]">
            <img
              src="/images/B01_Manjar.webp"
              alt="Brazo gitano de manjar elaborado artesanalmente por Brazolovers en Quito"
              className="w-full h-full object-cover"
              loading="eager"
              decoding="async"
            />
          </div>
        </section>

        {/* Cifras */}
        <section className="container mx-auto px-4 md:px-6 pb-12">
          <div className="max-w-3xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { valor: '2020', etiqueta: 'año en que empezó, en la cocina de casa' },
              { valor: '+40', etiqueta: 'sabores de brazo gitano' },
              { valor: `${locations.length}`, etiqueta: 'locales en Quito' },
              { valor: resenas.rating.toFixed(1), etiqueta: `en Google, sobre ${resenas.total} reseñas` },
            ].map((c) => (
              <div key={c.etiqueta} className="bg-white rounded-2xl shadow-sm p-5 text-center">
                <div className="text-3xl font-bold text-caramel">{c.valor}</div>
                <div className="text-sm text-chocolate-light mt-1">{c.etiqueta}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Historia real — solo si está cargada (ver HISTORIA arriba) */}
        {HISTORIA && (
          <section className="container mx-auto px-4 md:px-6 pb-12">
            <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm p-6 md:p-8 space-y-4">
              <h2 className="text-2xl md:text-3xl font-bold text-chocolate">Nuestra historia</h2>
              {HISTORIA.parrafos.map((p, i) => (
                <p key={i} className="text-chocolate-light leading-relaxed text-lg text-justify hyphens-auto">{p}</p>
              ))}
              {HISTORIA.hitos?.length > 0 && (
                <ol className="border-l-2 border-caramel/30 pl-5 space-y-4 pt-2">
                  {HISTORIA.hitos.map((h) => (
                    <li key={h.anio} className="relative">
                      <span className="absolute -left-[27px] top-1.5 w-3 h-3 rounded-full bg-caramel" aria-hidden="true" />
                      <div className="font-bold text-chocolate">{h.anio}</div>
                      <div className="text-chocolate-light">{h.texto}</div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </section>
        )}

        {/* Qué es un brazo gitano — contenido educativo + SEO local */}
        <section className="container mx-auto px-4 md:px-6 pb-12">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm p-6 md:p-8 space-y-4">
            <div className="flex items-center gap-3">
              <Wheat className="w-6 h-6 text-caramel shrink-0" />
              <h2 className="text-2xl md:text-3xl font-bold text-chocolate">¿Qué es un brazo gitano?</h2>
            </div>
            <p className="text-chocolate-light leading-relaxed text-lg text-justify hyphens-auto">
              Es un bizcocho fino y esponjoso que se hornea en plancha, se rellena y se enrolla. Nació
              en la repostería española y cruzó el Atlántico hasta volverse un clásico
              latinoamericano: en otros países lo llaman pionono o brazo de reina, pero en Ecuador
              se lo conoce como <strong>brazo gitano</strong>.
            </p>
            <p className="text-chocolate-light leading-relaxed text-lg text-justify hyphens-auto">
              Nosotros respetamos esa técnica y le damos vuelta al relleno. Junto a los clásicos —manjar,
              chocolate, encanelado— trabajamos con fruta de temporada del país (guanábana, maracuyá,
              guayaba, piña, mora) y con sabores que reconoce cualquiera que creció aquí, como el
              manicho o el pirulín. De ahí salen los más de 40 sabores.
            </p>
          </div>
        </section>

        {/* Cómo trabajamos */}
        <section className="container mx-auto px-4 md:px-6 pb-12">
          <div className="max-w-3xl mx-auto space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold text-chocolate">Cómo trabajamos</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl shadow-sm p-5">
                <h3 className="font-bold text-chocolate mb-1">Fresco cada día</h3>
                <p className="text-sm text-chocolate-light">Producción diaria en lotes pequeños. Nada se hornea para quedarse en vitrina.</p>
              </div>
              <div className="bg-white rounded-2xl shadow-sm p-5">
                <h3 className="font-bold text-chocolate mb-1">Todo a mano</h3>
                <p className="text-sm text-chocolate-light">Batido, relleno y enrollado uno por uno. Por eso cada pieza sale un poco distinta.</p>
              </div>
              <div className="bg-white rounded-2xl shadow-sm p-5">
                <h3 className="font-bold text-chocolate mb-1">Por encargo</h3>
                <p className="text-sm text-chocolate-light">Pasteles personalizados y pedidos grandes con al menos 24 horas de anticipación.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Dónde estamos */}
        <section className="container mx-auto px-4 md:px-6 pb-12">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm p-6 md:p-8 space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold text-chocolate">Dónde nos encuentras</h2>
            <ul className="space-y-3">
              {locations.map((loc) => (
                <li key={loc.id} className="flex items-start gap-3 text-chocolate-light">
                  <MapPin className="w-5 h-5 mt-1 shrink-0 text-caramel" />
                  {/* Sin calificación por local a proposito: la de arriba viene
                      del API y es la agregada de los dos. Repetir aqui los
                      numeros estaticos de mock.js daria cifras que no cuadran. */}
                  <span>
                    <strong className="text-chocolate">{loc.name}:</strong> {loc.address}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex items-start gap-3 text-sm text-chocolate-light bg-cream/60 rounded-lg p-3">
              <Truck className="w-5 h-5 mt-0.5 shrink-0 text-caramel" />
              <span>
                <strong>{productionCenter.name}:</strong> {productionCenter.address}
                <span className="block opacity-80">{productionCenter.note}</span>
              </span>
            </div>
            <Link to="/ubicaciones/" className="inline-flex items-center min-h-[44px] text-caramel font-medium hover:underline">
              Ver ubicaciones y horarios →
            </Link>
          </div>
        </section>

        {/* Cierre */}
        <section className="container mx-auto px-4 md:px-6 pb-16">
          <div className="max-w-3xl mx-auto bg-chocolate-dark text-white rounded-2xl p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4 md:justify-between">
            <div>
              <h2 className="text-2xl font-bold">¿Nos pruebas?</h2>
              <p className="text-white/80">Revisa el menú completo o escríbenos y armamos tu pedido.</p>
            </div>
            <div className="flex gap-3 shrink-0">
              <Link to="/" className="inline-flex items-center justify-center min-h-[44px] px-5 rounded-full bg-caramel text-white font-semibold hover:opacity-90 transition-opacity">
                Ver el menú
              </Link>
              <Link to="/contacto/" className="inline-flex items-center justify-center min-h-[44px] px-5 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors">
                Contacto
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer businessInfo={businessInfo} />
    </div>
  );
};

export default Nosotros;
